import { OrderedEvents } from "./events";
export async function startRun(base, session, ownerToken, payload) {
    const response = await fetch(`${base}/chat/v2/runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-NOA-Session": session,
            ...(ownerToken ? { Authorization: `Bearer ${ownerToken}` } : {}) },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok)
        throw new Error(data.detail || data.code || "Unable to start conversation.");
    return data;
}
/** Upgrade a receipt saved by an older widget once, without polling or resubmitting. */
export async function resolveStreamReceipt(base, run) {
    if (run.conversation_stream_token)
        return run;
    const response = await fetch(`${base}/chat/v2/runs/${run.run_id}`, { headers: { "X-NOA-Run": run.stream_token } });
    if (!response.ok)
        throw new Error("Unable to reconnect to conversation.");
    return response.json();
}
/** Constructed lazily after sending; a single socket serves successive customer turns. */
export class ConversationTransport {
    constructor(base, conversationId, token, debug) {
        this.base = base;
        this.conversationId = conversationId;
        this.debug = debug;
        this.subscriptions = new Map();
        this.failures = 0;
        this.visible = true;
        this.disposed = false;
        this.token = token;
    }
    get isClosed() { return this.disposed; }
    setDebug(debug) {
        var _a, _b;
        const changed = ((_a = this.debug) === null || _a === void 0 ? void 0 : _a.ownerToken) !== (debug === null || debug === void 0 ? void 0 : debug.ownerToken);
        this.debug = debug;
        if (changed && ((_b = this.socket) === null || _b === void 0 ? void 0 : _b.readyState) === 1)
            this.send({ type: "debug.authorize", token: (debug === null || debug === void 0 ? void 0 : debug.ownerToken) || "" });
    }
    subscribe(run, receive) {
        var _a;
        if (this.disposed || run.conversation_id !== this.conversationId || !run.conversation_stream_token) {
            throw new Error("Conversation stream is unavailable.");
        }
        this.token = run.conversation_stream_token;
        this.remove(run.run_id, new Error("Conversation subscription replaced."));
        // Only the previous successful turn's suggestion window needs a second subscription.
        for (const [id, previous] of this.subscriptions) {
            if (this.subscriptions.size < 2)
                break;
            if (previous.terminal)
                this.remove(id);
        }
        if (this.subscriptions.size >= 2)
            throw new Error("Conversation is already responding.");
        let resolve;
        let reject;
        const completed = new Promise((yes, no) => { resolve = yes; reject = no; });
        const subscription = {
            run, receive, resolve, reject, terminal: false, replaying: false, replayCursor: -1, replayAttempts: 0,
            order: new OrderedEvents(run.run_id, (event) => {
                receive(event);
                if (["run.completed", "run.failed", "run.interrupted"].includes(event.type))
                    this.finish(subscription, event.type);
            }, run.replay_after_seq || 0),
        };
        this.subscriptions.set(run.run_id, subscription);
        subscription.deadline = setTimeout(() => {
            this.remove(run.run_id, new Error("Conversation response timed out. Reconnect to review its saved state."));
        }, (run.timeout_seconds || 480) * 1000);
        clearTimeout(this.idleTimer);
        this.idleTimer = undefined;
        if (((_a = this.socket) === null || _a === void 0 ? void 0 : _a.readyState) === 1)
            this.replay(subscription);
        else
            this.connect();
        return { completed, close: () => this.remove(run.run_id, new Error("Conversation stream closed.")) };
    }
    setVisible(visible) {
        this.visible = visible;
        if (!visible && !this.subscriptions.size)
            this.disconnect();
    }
    close() {
        if (this.disposed)
            return;
        this.disposed = true;
        for (const id of [...this.subscriptions.keys()])
            this.remove(id, new Error("Conversation stream closed."));
        this.disconnect();
    }
    send(payload) {
        var _a;
        if (((_a = this.socket) === null || _a === void 0 ? void 0 : _a.readyState) !== 1)
            return;
        try {
            this.socket.send(JSON.stringify(payload));
        }
        catch {
            this.lostConnection(this.socket);
        }
    }
    replay(subscription) {
        var _a;
        if (subscription.replaying || ((_a = this.socket) === null || _a === void 0 ? void 0 : _a.readyState) !== 1)
            return;
        if (subscription.replayCursor !== subscription.order.cursor) {
            subscription.replayCursor = subscription.order.cursor;
            subscription.replayAttempts = 0;
        }
        if (++subscription.replayAttempts > 3) {
            this.lostConnection(this.socket);
            return;
        }
        subscription.replaying = true;
        this.send({ type: "subscribe", run_id: subscription.run.run_id, after_seq: subscription.order.cursor });
    }
    finish(subscription, type) {
        if (subscription.terminal)
            return;
        subscription.terminal = true;
        clearTimeout(subscription.deadline);
        subscription.resolve();
        if ([...this.subscriptions.values()].every(item => item.terminal))
            this.scheduleIdle();
        if (type === "run.completed")
            subscription.stop = setTimeout(() => this.remove(subscription.run.run_id), 30000);
        else
            this.remove(subscription.run.run_id);
    }
    remove(id, error) {
        const subscription = this.subscriptions.get(id);
        if (!subscription)
            return;
        clearTimeout(subscription.deadline);
        clearTimeout(subscription.stop);
        this.subscriptions.delete(id);
        this.send({ type: "unsubscribe", run_id: id });
        if (!subscription.terminal)
            subscription.reject(error || new Error("Conversation stream closed."));
        if (!this.subscriptions.size) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
            if (this.disposed || !this.visible)
                this.disconnect();
            else if (!this.idleTimer)
                this.scheduleIdle();
        }
    }
    scheduleIdle() {
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => this.disconnect(), 300000);
    }
    disconnect() {
        clearTimeout(this.reconnectTimer);
        clearTimeout(this.heartbeatTimer);
        clearTimeout(this.pongTimer);
        clearTimeout(this.connectTimer);
        clearTimeout(this.idleTimer);
        this.idleTimer = undefined;
        this.reconnectTimer = undefined;
        const socket = this.socket;
        this.socket = undefined;
        socket === null || socket === void 0 ? void 0 : socket.close();
    }
    connect() {
        if (this.disposed || this.socket || this.reconnectTimer || !this.subscriptions.size)
            return;
        const url = new URL(`${this.base}/chat/v2/ws/conversations/${this.conversationId}/`, window.location.href);
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        url.searchParams.set("token", this.token);
        try {
            const socket = this.socket = new WebSocket(url);
            this.connectTimer = setTimeout(() => this.lostConnection(socket), 10000);
            socket.onopen = () => {
                var _a;
                if (this.socket !== socket)
                    return;
                clearTimeout(this.connectTimer);
                if ((_a = this.debug) === null || _a === void 0 ? void 0 : _a.ownerToken)
                    this.send({ type: "debug.authorize", token: this.debug.ownerToken });
                for (const subscription of this.subscriptions.values())
                    this.replay(subscription);
                this.heartbeat();
            };
            socket.onmessage = (message) => {
                if (this.socket !== socket)
                    return;
                try {
                    this.receive(JSON.parse(message.data));
                }
                catch {
                    this.lostConnection(socket);
                }
            };
            socket.onerror = () => this.lostConnection(socket);
            socket.onclose = (event) => {
                if (this.socket !== socket)
                    return;
                if ([4400, 4403].includes(event.code))
                    this.close();
                else
                    this.lostConnection(socket);
            };
        }
        catch {
            this.lostConnection();
        }
    }
    lostConnection(socket) {
        if (socket && socket !== this.socket)
            return;
        this.disconnect();
        for (const subscription of this.subscriptions.values()) {
            subscription.replaying = false;
            subscription.replayAttempts = 0;
        }
        if (this.disposed || !this.subscriptions.size)
            return;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.connect();
        }, Math.min(10000, 1000 * 2 ** Math.min(this.failures++, 4)));
    }
    heartbeat() {
        clearTimeout(this.heartbeatTimer);
        this.heartbeatTimer = setTimeout(() => {
            var _a;
            if (((_a = this.socket) === null || _a === void 0 ? void 0 : _a.readyState) !== 1)
                return;
            this.send({ type: "ping" });
            this.pongTimer = setTimeout(() => this.lostConnection(this.socket), 10000);
        }, 25000);
    }
    receive(frame) {
        var _a, _b, _c, _d;
        if ((frame === null || frame === void 0 ? void 0 : frame.type) === "socket.ready")
            return;
        if ((frame === null || frame === void 0 ? void 0 : frame.type) === "pong") {
            clearTimeout(this.pongTimer);
            this.heartbeat();
            return;
        }
        if ((frame === null || frame === void 0 ? void 0 : frame.type) === "debug.authorized")
            return;
        if ((frame === null || frame === void 0 ? void 0 : frame.type) === "debug.denied") {
            for (const id of this.subscriptions.keys())
                (_a = this.debug) === null || _a === void 0 ? void 0 : _a.receive(id, null);
            return;
        }
        const subscription = this.subscriptions.get(frame === null || frame === void 0 ? void 0 : frame.run_id);
        if (!subscription)
            return;
        if (["debug.snapshot", "debug.updated"].includes(frame.type)) {
            if (((_b = this.debug) === null || _b === void 0 ? void 0 : _b.ownerToken) && ((_d = (_c = frame.data) === null || _c === void 0 ? void 0 : _c.run) === null || _d === void 0 ? void 0 : _d.run_id) === frame.run_id
                && frame.data.run.conversation_id === this.conversationId)
                this.debug.receive(frame.run_id, frame.data);
            return;
        }
        if (frame.type === "run.snapshot") {
            subscription.replaying = false;
            if (!Number.isInteger(frame.last_seq) || frame.last_seq < subscription.order.cursor)
                return;
            if (frame.last_seq > subscription.order.cursor) {
                this.replay(subscription);
                return;
            }
            this.failures = 0;
            subscription.replayAttempts = 0;
            if (!subscription.terminal && ["completed", "failed", "interrupted"].includes(frame.status)) {
                const recovered = (type, payload) => ({
                    version: 2, run_id: subscription.run.run_id, seq: subscription.order.cursor,
                    type, created_at: new Date().toISOString(), payload: { ...payload, recovered_state: true },
                });
                if (typeof frame.answer === "string")
                    subscription.receive(recovered("text.completed", { text: frame.answer, message_id: frame.message_id }));
                subscription.receive(recovered(`run.${frame.status}`, { code: frame.error }));
                this.finish(subscription, `run.${frame.status}`);
            }
            return;
        }
        subscription.order.push(frame);
        if (subscription.terminal && [...this.subscriptions.values()].every(item => item.terminal))
            this.scheduleIdle();
        if (subscription.order.hasGap)
            this.replay(subscription);
    }
}
